import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { styled } from '@mui/material/styles';
import {
  Box,
  Typography,
  Container,
  Button,
  Grid,
  Card,
  CardContent,
  Avatar,
  useMediaQuery,
  useTheme,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Fab
} from '@mui/material';
import {
  QrCode2,
  RestaurantMenu,
  Smartphone,
  Speed,
  Analytics,
  Payment,
  Kitchen,
  TableRestaurant,
  Receipt,
  Settings,
  ExpandMore,
  CheckCircle,
  Star,
  TrendingUp,
  Security,
  Support
} from '@mui/icons-material';
import Header from './components/Header';
import Footer from './components/Footer';
import PricingSection from './components/PricingSection';
import PublicAiChatWidget from './components/PublicAiChatWidget';
import { useTranslation } from 'react-i18next';
import { BRAND, BRAND_GRADIENTS } from '../../config/brand';
import { usePublicRegistrationFlow } from '../../contexts/PublicRegistrationFlowContext';

// Stil tanımlamaları
const PageContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  height: '100vh',
  background: BRAND_GRADIENTS.primary, // BRAND'den
}));

const ContentWrapper = styled(Box)({
  flex: 1,
  overflowY: 'auto',
  overflowX: 'hidden',
});

const HeroSection = styled(Box)(({ theme }) => ({
  background: 'linear-gradient(135deg, rgba(13, 71, 161, 0.9) 0%, rgba(25, 118, 210, 0.8) 100%)',
  padding: theme.spacing(8, 0),
  minHeight: '35vh',
  display: 'flex',
  alignItems: 'center',
  textAlign: 'center',
  color: 'white',
  position: 'relative',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.05"%3E%3Ccircle cx="30" cy="30" r="2"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
    opacity: 0.3
  }
}));

const FeatureCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  transition: 'all 0.3s ease-in-out',
  '&:hover': {
    transform: 'translateY(-8px)',
    boxShadow: theme.shadows[8],
  },
  background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
}));

const StatsSection = styled(Box)(({ theme }) => ({
  background: 'rgba(255, 255, 255, 0.1)',
  backdropFilter: 'blur(10px)',
  borderRadius: theme.spacing(2),
  padding: theme.spacing(4),
  margin: theme.spacing(2, 0),
  border: '1px solid rgba(255, 255, 255, 0.2)'
}));

const ScrollProgressBar = styled(Box)(({ theme }) => ({
  position: 'fixed',
  top: 0,
  left: 0,
  width: '100%',
  height: '4px',
  backgroundColor: 'rgba(255, 255, 255, 0.1)',
  zIndex: 1001,
  backdropFilter: 'blur(10px)',
  '&::after': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    height: '100%',
    background: BRAND_GRADIENTS.horizontal, // BRAND'den
    transition: 'width 0.15s ease-out',
    boxShadow: '0 0 15px rgba(13, 71, 161, 0.6)',
    borderRadius: '0 2px 2px 0',
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '1px',
    background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.3) 50%, transparent 100%)',
    animation: 'shimmer 2s infinite',
  },
  '@keyframes shimmer': {
    '0%': { transform: 'translateX(-100%)' },
    '100%': { transform: 'translateX(100%)' }
  }
}));

const QRMenu: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const { openRoleSelection } = usePublicRegistrationFlow();
  const [scrollProgress, setScrollProgress] = React.useState(0);

  // Scroll event listener
  React.useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const scrollPercent = (scrollTop / docHeight) * 100;
      
      setScrollProgress(scrollPercent);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const features = [
    {
      icon: <QrCode2 />,
      title: t('qrMenu.features.qrGeneration.title'),
      description: t('qrMenu.features.qrGeneration.description'),
      color: BRAND.colors.secondary
    },
    {
      icon: <RestaurantMenu />,
      title: t('qrMenu.features.menuManagement.title'),
      description: t('qrMenu.features.menuManagement.description'),
      color: '#2E7D32'
    },
    {
      icon: <Smartphone />,
      title: t('qrMenu.features.mobileOptimized.title'),
      description: t('qrMenu.features.mobileOptimized.description'),
      color: '#ED6C02'
    },
    {
      icon: <Kitchen />,
      title: t('qrMenu.features.kitchenScreen.title'),
      description: t('qrMenu.features.kitchenScreen.description'),
      color: '#9C27B0'
    },
    {
      icon: <Receipt />,
      title: t('qrMenu.features.orderManagement.title'),
      description: t('qrMenu.features.orderManagement.description'),
      color: '#D32F2F'
    },
    {
      icon: <Analytics />,
      title: t('qrMenu.features.analytics.title'),
      description: t('qrMenu.features.analytics.description'),
      color: '#1565C0'
    },
    {
      icon: <Payment />, // örnek: gelişmiş ön muhasebe modülü
      title: 'Gelişmiş Ön Muhasebe Modülü',
      description: 'Tüm finansal süreçlerinizi kolayca yönetin.',
      color: '#9C27B0',
      altDescription: (
        <>
          <span style={{ color: '#bdbdbd', fontSize: '0.95em', display: 'block', marginTop: 8, textAlign: 'left' }}>
            – Cari Takip<br/>
            – Gider–Gelir Yönetimi<br/>
            – Banka Hesap Takibi<br/>
            – Tahsilat & Ödeme Planları
          </span>
        </>
      )
    }
  ];

  const stats = [
    { number: '500+', label: t('qrMenu.stats.activeMenus') },
    { number: '10K+', label: t('qrMenu.stats.monthlyOrders') },
    { number: '99.9%', label: t('qrMenu.stats.uptime') },
    { number: '7/24', label: t('qrMenu.stats.support') }
  ];

  const pricingPlans = [
    {
      name: t('qrMenu.pricing.starter.name'),
      price: t('qrMenu.pricing.starter.price'),
      period: t('qrMenu.pricing.starter.period'),
      description: t('qrMenu.pricing.starter.description'),
      features: t('qrMenu.pricing.starter.features', { returnObjects: true }) as string[],
      buttonText: t('qrMenu.pricing.buttonText'),
      action: 'register' as const,
      popular: false
    },
    {
      name: t('qrMenu.pricing.professional.name'),
      price: t('qrMenu.pricing.professional.price'),
      period: t('qrMenu.pricing.professional.period'),
      description: t('qrMenu.pricing.professional.description'),
      features: t('qrMenu.pricing.professional.features', { returnObjects: true }) as string[],
      buttonText: t('qrMenu.pricing.buttonText'),
      action: 'register' as const,
      popular: true
    },
    {
      name: t('qrMenu.pricing.enterprise.name'),
      price: t('qrMenu.pricing.enterprise.price'),
      period: t('qrMenu.pricing.enterprise.period'),
      description: t('qrMenu.pricing.enterprise.description'),
      features: t('qrMenu.pricing.enterprise.features', { returnObjects: true }) as string[],
      buttonText: t('qrMenu.pricing.contactButtonText'),
      action: 'contact' as const,
      popular: false
    }
  ];

  const benefits = [
    t('qrMenu.benefits.contactless'),
    t('qrMenu.benefits.realTime'),
    t('qrMenu.benefits.customizable'),
    t('qrMenu.benefits.integrated'),
    t('qrMenu.benefits.analytics'),
    t('qrMenu.benefits.multiLanguage')
  ];

  const faqs = [
    {
      question: t('qrMenu.faq.q1.question'),
      answer: t('qrMenu.faq.q1.answer')
    },
    {
      question: t('qrMenu.faq.q2.question'),
      answer: t('qrMenu.faq.q2.answer')
    },
    {
      question: t('qrMenu.faq.q3.question'),
      answer: t('qrMenu.faq.q3.answer')
    },
    {
      question: t('qrMenu.faq.q4.question'),
      answer: t('qrMenu.faq.q4.answer')
    }
  ];

  // Smooth scroll function
  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  };

  return (
    <PageContainer>
      <Helmet>
        <title>{t('qrMenu.meta.title')}</title>
        <meta name="description" content={t('qrMenu.meta.description')} />
        <meta name="keywords" content={t('qrMenu.meta.keywords')} />
      </Helmet>
      
      <Header />

      <ContentWrapper>
        {/* Hero Section */}
        <HeroSection>
          <Container maxWidth="lg">
            <Typography 
              variant="h1" 
              sx={{ 
                fontSize: { xs: '1.8rem', sm: '2.5rem', md: '3rem' },
                fontWeight: 800,
                mb: 2,
                background: 'linear-gradient(45deg, #ffffff 30%, #e3f2fd 90%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}
            >
              {t('qrMenu.hero.title')}
            </Typography>
            <Typography 
              variant="h4" 
              sx={{ 
                fontSize: { xs: '0.9rem', sm: '1.1rem', md: '1.4rem' },
                fontWeight: 400,
                mb: 4,
                opacity: 0.9,
                maxWidth: '800px',
                mx: 'auto'
              }}
            >
              {t('qrMenu.hero.subtitle')}
            </Typography>
            
            <Box sx={{ mb: 4 }}>
              <Button
                variant="contained"
                size="large"
                sx={{
                  mr: 2,
                  mb: { xs: 2, sm: 0 },
                  px: 4,
                  py: 1.5,
                  fontSize: '1.1rem',
                  backgroundColor: 'white',
                  color: BRAND.colors.primary,
                  '&:hover': {
                    backgroundColor: '#f5f5f5',
                    transform: 'translateY(-2px)'
                  }
                }}
                onClick={() => openRoleSelection()}
              >
                {t('qrMenu.hero.tryFreeButton')}
              </Button>
              <Button
                variant="contained"
                size="large"
                sx={{
                  px: 4,
                  py: 1.5,
                  fontSize: '1.1rem',
                  backgroundColor: 'white',
                  color: BRAND.colors.primary,
                  '&:hover': {
                    backgroundColor: '#f5f5f5',
                    transform: 'translateY(-2px)'
                  }
                }}
                onClick={() => navigate('/contact')}
              >
                {t('qrMenu.hero.demoButton')}
              </Button>
            </Box>

            {/* Quick Navigation */}
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2.5, flexWrap: 'wrap', mb: 4 }}>
              <Button
                variant="text"
                size="small"
                sx={{
                  color: 'rgba(255,255,255,0.9)',
                  fontSize: { xs: '0.82rem', sm: '0.9rem', md: '0.95rem' },
                  fontWeight: 700,
                  px: 1.2,
                  py: 0.6,
                  '&:hover': { color: 'white' }
                }}
                onClick={() => scrollToSection('features')}
              >
                Özellikler
              </Button>
              <Button
                variant="text"
                size="small"
                sx={{
                  color: 'rgba(255,255,255,0.9)',
                  fontSize: { xs: '0.82rem', sm: '0.9rem', md: '0.95rem' },
                  fontWeight: 700,
                  px: 1.2,
                  py: 0.6,
                  '&:hover': { color: 'white' }
                }}
                onClick={() => scrollToSection('benefits')}
              >
                Avantajlar
              </Button>
              <Button
                variant="text"
                size="small"
                sx={{
                  color: 'rgba(255,255,255,0.9)',
                  fontSize: { xs: '0.82rem', sm: '0.9rem', md: '0.95rem' },
                  fontWeight: 700,
                  px: 1.2,
                  py: 0.6,
                  '&:hover': { color: 'white' }
                }}
                onClick={() => scrollToSection('faq')}
              >
                SSS
              </Button>
              <Button
                variant="text"
                size="small"
                sx={{
                  color: 'rgba(255,255,255,0.9)',
                  fontSize: { xs: '0.82rem', sm: '0.9rem', md: '0.95rem' },
                  fontWeight: 700,
                  px: 1.2,
                  py: 0.6,
                  '&:hover': { color: 'white' }
                }}
                onClick={() => scrollToSection('cta')}
              >
                Başlayın
              </Button>
            </Box>

            {/* Stats */}
            <StatsSection>
              <Grid container spacing={4}>
                {stats.map((stat, index) => (
                  <Grid item xs={6} md={3} key={index}>
                    <Box textAlign="center">
                      <Typography 
                        variant="h3" 
                        sx={{ 
                          fontWeight: 800, 
                          color: 'white',
                          fontSize: { xs: '1rem', md: '1.8rem' }
                        }}
                      >
                        {stat.number}
                      </Typography>
                      <Typography 
                        variant="body1" 
                        sx={{ 
                          color: 'rgba(255, 255, 255, 0.8)',
                          fontSize: { xs: '0.7rem', md: '0.8rem' }
                        }}
                      >
                        {stat.label}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </StatsSection>
          </Container>
        </HeroSection>

        {/* Pricing Section */}
        <PricingSection 
          backgroundColor="white"
          titleColor={BRAND.colors.primary}
          source="qrmenu_pricing"
          pricingPlans={pricingPlans}
          title={t('qrMenu.pricing.title')}
        />

        {/* Features Section */}
        <Box id="features" sx={{ py: 8, backgroundColor: 'white' }}>
          <Container maxWidth="lg">
            <Typography 
              variant="h2" 
              textAlign="center" 
              sx={{ 
                mb: 6,
                fontWeight: 700,
                color: BRAND.colors.primary,
                fontSize: { xs: '2rem', md: '3rem' }
              }}
            >
              {t('qrMenu.features.title')}
            </Typography>
            <Grid container spacing={4}>
              {features.map((feature, index) => (
                <Grid item xs={12} md={4} key={index}>
                  <FeatureCard>
                    <CardContent sx={{ flexGrow: 1, textAlign: 'center', p: 3 }}>
                      <Avatar
                        sx={{
                          backgroundColor: feature.color,
                          width: 64,
                          height: 64,
                          mx: 'auto',
                          mb: 2
                        }}
                      >
                        {feature.icon}
                      </Avatar>
                      <Typography 
                        variant="h5" 
                        gutterBottom 
                        sx={{ fontWeight: 600, color: '#333' }}
                      >
                        {feature.title}
                      </Typography>
                      <Typography 
                        variant="body1" 
                        sx={{ color: '#666', lineHeight: 1.6 }}
                      >
                        {feature.description}
                      </Typography>
                      {feature.altDescription && (
                        <Typography
                          variant="body2"
                          sx={{ color: '#bdbdbd', fontSize: '0.95em', mt: 1, textAlign: 'left' }}
                          component="div"
                        >
                          {feature.altDescription}
                        </Typography>
                      )}
                    </CardContent>
                  </FeatureCard>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>

        <Footer />
      </ContentWrapper>

      {/* WhatsApp Floating Button */}
      <PublicAiChatWidget />
    </PageContainer>
  );
};

export default QRMenu;
